export type SelectAllCheckboxSetters = {
  setSelectAll: (value: boolean) => void;
  setCheckedList: (list: string[]) => void;
  setUncheckList: (list: string[]) => void;
};

export function uniqueSelectionIds(ids: string[]): string[] {
  return [...new Set(ids)];
}

export function isSelectAllRowSelected(
  id: string,
  selectAll: boolean,
  checkedList: string[],
  uncheckList: string[]
): boolean {
  if (selectAll) {
    return !uncheckList.includes(id);
  }
  return checkedList.includes(id);
}

export function getVisibleSelectedKeys(
  rowIds: string[],
  selectAll: boolean,
  checkedList: string[],
  uncheckList: string[]
): string[] {
  if (selectAll) {
    return rowIds.filter((id) => !uncheckList.includes(id));
  }
  return rowIds.filter((id) => checkedList.includes(id));
}

export function getHeaderCheckboxState({
  selectAll,
  checkedList,
  uncheckList,
  visibleIds,
  total,
  hasActiveSearch,
}: {
  selectAll: boolean;
  checkedList: string[];
  uncheckList: string[];
  visibleIds: string[];
  total: number;
  hasActiveSearch: boolean;
}): { checked: boolean; indeterminate: boolean } {
  if (hasActiveSearch) {
    const selectedVisible = visibleIds.filter((id) =>
      isSelectAllRowSelected(id, selectAll, checkedList, uncheckList)
    ).length;
    const visibleCount = visibleIds.length;
    return {
      checked: visibleCount > 0 && selectedVisible === visibleCount,
      indeterminate: selectedVisible > 0 && selectedVisible < visibleCount,
    };
  }

  const selectedCount = selectAll
    ? Math.max(0, total - uncheckList.length)
    : checkedList.length;

  return {
    checked: total > 0 && selectedCount === total,
    indeterminate: selectedCount > 0 && selectedCount < total,
  };
}

export function applyRowCheckboxChange({
  rowId,
  checked,
  selectAll,
  checkedList,
  uncheckList,
  total,
  hasActiveSearch,
  setters,
}: {
  rowId: string;
  checked: boolean;
  selectAll: boolean;
  checkedList: string[];
  uncheckList: string[];
  total: number;
  hasActiveSearch: boolean;
  setters: SelectAllCheckboxSetters;
}): void {
  if (checked) {
    if (selectAll) {
      setters.setUncheckList(uncheckList.filter((key) => key !== rowId));
      return;
    }

    const nextChecked = uniqueSelectionIds([...checkedList, rowId]);
    if (!hasActiveSearch && total > 0 && nextChecked.length >= total) {
      setters.setSelectAll(true);
      setters.setCheckedList([]);
      setters.setUncheckList([]);
      return;
    }

    setters.setCheckedList(nextChecked);
    return;
  }

  if (selectAll) {
    const nextUnchecked = uniqueSelectionIds([...uncheckList, rowId]);
    if (!hasActiveSearch && total > 0 && nextUnchecked.length >= total) {
      setters.setSelectAll(false);
      setters.setCheckedList([]);
      setters.setUncheckList([]);
      return;
    }
    setters.setUncheckList(nextUnchecked);
    return;
  }

  const nextChecked = checkedList.filter((key) => key !== rowId);
  setters.setCheckedList(nextChecked);
  if (nextChecked.length === 0) {
    setters.setSelectAll(false);
    setters.setUncheckList([]);
  }
}

export function applyHeaderCheckboxChange({
  checked,
  selectAll,
  checkedList,
  uncheckList,
  visibleIds,
  hasActiveSearch,
  setters,
}: {
  checked: boolean;
  selectAll: boolean;
  checkedList: string[];
  uncheckList: string[];
  visibleIds: string[];
  hasActiveSearch: boolean;
  setters: SelectAllCheckboxSetters;
}): void {
  if (hasActiveSearch && !selectAll) {
    if (checked) {
      setters.setCheckedList(
        uniqueSelectionIds([...checkedList, ...visibleIds])
      );
      return;
    }
    setters.setCheckedList(
      checkedList.filter((id) => !visibleIds.includes(id))
    );
    return;
  }

  if (hasActiveSearch && selectAll) {
    if (checked) {
      setters.setUncheckList(
        uncheckList.filter((id) => !visibleIds.includes(id))
      );
      return;
    }
    setters.setUncheckList(uniqueSelectionIds([...uncheckList, ...visibleIds]));
    return;
  }

  if (checked) {
    setters.setSelectAll(true);
    setters.setCheckedList([]);
    setters.setUncheckList([]);
    return;
  }

  setters.setSelectAll(false);
  setters.setCheckedList([]);
  setters.setUncheckList([]);
}
